import type { Scene } from "@babylonjs/core/scene";

/**
 * Procedural ambient bed via Web Audio: a wind layer and a water layer (filtered
 * noise with slow LFOs) plus occasional positional birdsong (PannerNode). No
 * asset files, no music track. Starts on the first user gesture (autoplay
 * policy) and reads — never writes — the camera to place the listener.
 */
export class AmbientAudio {
  private ctx: AudioContext | null = null;
  private started = false;
  private birdTimer = 0;

  constructor(private readonly scene: Scene) {
    const start = (): void => this.start();
    window.addEventListener("pointerdown", start, { once: true });
    window.addEventListener("keydown", start, { once: true });
  }

  start(): void {
    if (this.started) return;
    this.started = true;
    const Ctx = window.AudioContext ?? (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
    if (!Ctx) return;
    const ctx = new Ctx();
    this.ctx = ctx;

    const master = ctx.createGain();
    master.gain.value = 0.5;
    master.connect(ctx.destination);

    this.wind(ctx, master);
    this.water(ctx, master);

    // Update the listener from the camera each frame (read-only).
    this.scene.onBeforeRenderObservable.add(() => this.tick());
  }

  private noiseSource(ctx: AudioContext): AudioBufferSourceNode {
    const len = ctx.sampleRate * 2;
    const buf = ctx.createBuffer(1, len, ctx.sampleRate);
    const data = buf.getChannelData(0);
    let last = 0;
    for (let i = 0; i < len; i++) {
      const white = Math.random() * 2 - 1;
      last = (last + 0.02 * white) / 1.02; // brownish
      data[i] = last * 3.5;
    }
    const src = ctx.createBufferSource();
    src.buffer = buf;
    src.loop = true;
    src.start();
    return src;
  }

  private wind(ctx: AudioContext, out: GainNode): void {
    const src = this.noiseSource(ctx);
    const lp = ctx.createBiquadFilter();
    lp.type = "lowpass";
    lp.frequency.value = 420;
    const g = ctx.createGain();
    g.gain.value = 0.09;
    src.connect(lp).connect(g).connect(out);
    // slow gusts
    const lfo = ctx.createOscillator();
    lfo.frequency.value = 0.06;
    const lfoGain = ctx.createGain();
    lfoGain.gain.value = 0.05;
    lfo.connect(lfoGain).connect(g.gain);
    lfo.start();
  }

  private water(ctx: AudioContext, out: GainNode): void {
    const src = this.noiseSource(ctx);
    const bp = ctx.createBiquadFilter();
    bp.type = "bandpass";
    bp.frequency.value = 900;
    bp.Q.value = 0.6;
    const g = ctx.createGain();
    g.gain.value = 0.04;
    src.connect(bp).connect(g).connect(out);
  }

  private tick(): void {
    const ctx = this.ctx;
    if (!ctx) return;
    const cam = this.scene.activeCamera;
    if (cam) {
      const p = cam.position;
      const l = ctx.listener;
      if (l.positionX) {
        l.positionX.value = p.x;
        l.positionY.value = p.y;
        l.positionZ.value = p.z;
      }
    }
    // Occasional birdsong from a random point around the listener.
    this.birdTimer -= this.scene.getEngine().getDeltaTime() / 1000;
    if (this.birdTimer <= 0) {
      this.birdTimer = 4 + Math.random() * 7;
      this.chirp(ctx);
    }
  }

  private chirp(ctx: AudioContext): void {
    const cam = this.scene.activeCamera;
    const cx = cam?.position.x ?? 0;
    const cz = cam?.position.z ?? 0;
    const panner = ctx.createPanner();
    panner.panningModel = "HRTF";
    panner.distanceModel = "inverse";
    panner.refDistance = 3;
    const a = Math.random() * Math.PI * 2;
    const r = 4 + Math.random() * 10;
    panner.positionX.value = cx + Math.cos(a) * r;
    panner.positionY.value = 2 + Math.random() * 3;
    panner.positionZ.value = cz + Math.sin(a) * r;
    panner.connect(ctx.destination);

    const notes = 2 + ((Math.random() * 3) | 0);
    const base = 1800 + Math.random() * 1600;
    for (let n = 0; n < notes; n++) {
      const t = ctx.currentTime + n * (0.09 + Math.random() * 0.06);
      const osc = ctx.createOscillator();
      osc.type = "triangle";
      osc.frequency.setValueAtTime(base * (0.9 + Math.random() * 0.3), t);
      osc.frequency.exponentialRampToValueAtTime(base * 1.4, t + 0.06);
      const g = ctx.createGain();
      g.gain.setValueAtTime(0, t);
      g.gain.linearRampToValueAtTime(0.12, t + 0.01);
      g.gain.exponentialRampToValueAtTime(0.001, t + 0.11);
      osc.connect(g).connect(panner);
      osc.start(t);
      osc.stop(t + 0.14);
    }
  }
}
